<template>
  <div class="program-overview-container">
    <!-- Single Event -->
    <div
      v-if="programs.length === 1"
      class="program-single flex flex-column"
    >
      <div
        v-if="isProgramSoldOut(programs[0])"
        class="program-button flex align-center"
      >
        <button
          class="btn btn-primary disabled w-full mr-0 mb-sm"
          disabled
        >
          {{ programs[0].button?.text && programs[0].button.text.trim() ? programs[0].button.text : 'Uitverkocht' }}
        </button>
      </div>
      <div
        v-else-if="isProgramCancelled(programs[0])"
        class="program-button flex align-center"
      >
        <button
          class="btn btn-primary disabled w-full mr-0 mb-sm"
          disabled
        >
          {{ programs[0].button?.text && programs[0].button.text.trim() ? programs[0].button.text : 'Geannuleerd' }}
        </button>
      </div>
      <div
        v-else-if="isProgramNotYetOnSale(programs[0])"
        class="program-button flex align-center"
      >
        <button
          class="btn btn-primary disabled w-full m-0"
          disabled
        >
          Nog niet in verkoop
        </button>
      </div>
      <div
        v-else-if="!programs[0].button?.link"
        class="program-button flex align-center"
      >
        <UIContentblockButton
          class="btn btn-primary w-full mb-sm"
          :name="programSingle.button.text"
          :link="getEmbedUrl(programs[0])"
          :target="isExternalLink(programSingle.button.link) ? '_blank' : ''"
        />
      </div>
      <div
        v-else
        class="program-button flex align-center"
      >
        <NuxtLink
          class="btn btn-primary mr-0 mb-sm"
          :to="programs[0]?.button.link"
          :target="isExternalLink(programs[0].button.link) ? '_blank' : undefined"
        >
          {{ programs[0].button?.text }}
        </NuxtLink>
      </div>

      <div class="program-overview flex flex-column has-more-programs">
        <div
          v-for="item in programSingleReadable"
          :key="item.label"
          class="program-item"
        >
          <div
            class="program-label fw-700"
            v-text="item.label"
          />
          <div
            class="program-value"
            v-text="item.value"
          />
        </div>
      </div>
    </div>

    <!-- Multiple Events -->
    <div
      v-else
      class="program-multiple"
    >
      <div
        v-for="(program, index) in displayedPrograms"
        :key="index"
        class="program-card sm-mb-0 mb-sm"
      >
        <div class="flex flex-nowrap align-center justify-space-between mb-24">
          <h3 class="heading-5 mb-0">{{ formatDateEventDetail(program.startAt) }}</h3>
          <span
            v-for="attribute in program.attributes"
            :key="attribute.id"
            class="tag"
          >
            {{ attribute.name }}
          </span>
        </div>

        <div class="flex justify-space-between flex-gap-24 mb-sm">
          <p class="program-info time m-0">{{ formatTime(program.startAt) }}</p>
          <p class="program-price m-0">
            {{ formatEuro(program.price) }}
          </p>
        </div>

        <div class="flex justify-space-between flex-gap-24 mb-md">
          <p class="program-info location m-0">{{ program.fields.locationDetailed || program.location.name }}</p>
        </div>

        <div
          v-if="isProgramSoldOut(program)"
          class="btn-container flex"
        >
          <button
            class="btn btn-primary disabled w-full m-0"
            disabled
          >
            {{ program.button?.text && program.button.text.trim() ? program.button.text : 'Uitverkocht' }}
          </button>
        </div>

        <div
          v-else-if="isProgramCancelled(program)"
          class="btn-container flex"
        >
          <button
            class="btn btn-primary disabled w-full m-0"
            disabled
          >
            {{ program.button?.text && program.button.text.trim() ? program.button.text : 'Geannuleerd' }}
          </button>
        </div>

        <div
          v-else-if="isProgramNotYetOnSale(program)"
          class="btn-container flex"
        >
          <button
            class="btn btn-primary disabled w-full m-0"
            disabled
          >
            Nog niet in verkoop
          </button>
        </div>

        <div
          v-else
          class="btn-container flex"
        >
          <NuxtLink
            class="btn btn-primary w-full m-0"
            :to="getProgramLink(program)"
            :target="isExternalLink(getProgramLink(program)) ? '_blank' : undefined"
          >
            {{ program.button?.text }}
          </NuxtLink>
        </div>
      </div>

      <!-- Show All Button -->
      <div
        v-if="programs.length > 3"
        class="show-all-container flex justify-center mtb-md"
      >
        <button
          class="btn btn-secondary"
          @click="toggleShowAll"
        >
          {{ showAll ? 'Toon minder tijden' : 'Toon alle tijden' }}
        </button>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
  import type { OverviewEventProgram } from '~/types/events'

  const props = defineProps<{
    programSingle: Record<string, any>
    programSingleReadable: Array<{ label: string; value: string }>
    programs: OverviewEventProgram[]
  }>()

  const { formatTime, formatEuro, isProgramSoldOut, isProgramCancelled } = useEventsHelpers()

  const route = useRoute()
  const showAll = ref(false)

  console.log(props.programs[0])

  const getEmbedUrl = (program: any) => {
    const params = new URLSearchParams({
      id: program.fields.ticketId ? program.fields.ticketId : program.id,
      agenda: route.params.slug,
      programTime: program.startAt,
    })
    return `/programma/koop-kaarten?${params.toString()}`
  }

  const getProgramLink = (program: OverviewEventProgram) => {
    return program.button?.link || getEmbedUrl(program)
  }

  function isExternalLink(url: string) {
    return /^https?:\/\//.test(url) // Checks if the URL starts with 'http' or 'https'
  }

  function isProgramNotYetOnSale(program: OverviewEventProgram) {
    const prices = (program as OverviewEventProgram & { prices?: Array<{ saleStartAt: string }> }).prices
    if (!prices?.length) {
      return false
    }

    return prices.every((price) => new Date(price.saleStartAt) > new Date())
  }

  const displayedPrograms = computed(() => {
    return showAll.value ? props.programs : props.programs.slice(0, 3)
  })

  function toggleShowAll() {
    showAll.value = !showAll.value
  }

  const emit = defineEmits<{
    buyClick: [program: OverviewEventProgram]
  }>()
</script>

<style lang="scss" scoped>
  .program-overview-container {
    display: flex;
    flex-direction: column;
    gap: 2rem;
  }

  .program-card {
    padding: var(--padding-md);
    background-color: $accent;
    border-radius: 16px;
  }

  .program-button {
    .btn {
      width: 100%;
      margin-right: 0;
      margin-bottom: 4rem;
    }
  }

  .program-overview {
    //gap: 2.4rem;

    .program-label {
      color: rgba($black, 0.4);
      font-weight: 400;
    }

    .program-value {
      margin-bottom: 0;
    }

    .program-item {
      padding: var(--padding-md) 0;
      border-top: 1px solid rgba(0, 0, 0, 0.1);
    }
  }

  .program-multiple {
    .program-title {
      font-size: 2.8rem;
    }

    .tag {
      padding: 0.4rem 1.6rem;
      border-radius: 0.4rem;
      background-color: $black;
      color: $white;
      font-size: 1.6rem;
      white-space: nowrap;
    }

    .program-info {
      flex: 1 0 0;
    }

    .program-price {
      flex-basis: 45%;
    }
  }

  .program-multiple {
    padding: 0;
    display: flex;
    flex-direction: column;
    gap: 1.2rem;
  }
</style>
